import React from 'react';

import { Box, radioClasses, Typography } from '@mui/material';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { ActiveInactive } from 'src/api';
import { Button } from 'src/components/common';
import { Field, Form, RadioGroupField } from 'src/components/finalForm';
import { REQUEST_AUTO_APPROVE_OPTIONS } from 'src/constants/employer';
import { useAuthSignup } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';

import {
  ConsumerVerificationPreferenceFormFields,
  ConsumerVerificationPreferenceFormValues,
} from '../types';
import { consumerVerificationPreferenceFormValidationSchema } from '../utils';

export const ConsumerVerificationPreferenceForm: React.FC = () => {
  const { updateFormData, goToNextStep } = useAuthSignup();

  const initialValues: ConsumerVerificationPreferenceFormValues = React.useMemo(
    () => ({
      [ConsumerVerificationPreferenceFormFields.isAutoVerificationRequestEnabled]:
        ActiveInactive.Active,
    }),
    [],
  );

  const handleSubmit = React.useCallback(
    (formValues: ConsumerVerificationPreferenceFormValues) => {
      updateFormData({
        ...formValues,
      });
      goToNextStep();
    },
    [goToNextStep, updateFormData],
  );

  return (
    <Box>
      <Form<ConsumerVerificationPreferenceFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{ valid: true, values: true, submitting: true, errors: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(consumerVerificationPreferenceFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <Box sx={boxStyles} rowGap={2}>
              <Box>
                <Typography mb={1} variant="h5">
                  Verification Preference
                </Typography>
                <Typography mb={2} variant="body1" color="text.secondary" mt={1}>
                  Please select below whether you want to have verifications automatically approved
                  or if you want to manually manage this process
                </Typography>

                <Field
                  fullWidth
                  name={ConsumerVerificationPreferenceFormFields.isAutoVerificationRequestEnabled}
                  component={RadioGroupField}
                  options={REQUEST_AUTO_APPROVE_OPTIONS}
                  disabled={submitting}
                  sx={radioFieldStyles}
                />
              </Box>

              <Box sx={boxStyles} rowGap={2}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                  <Button
                    type="submit"
                    sx={submitButtonStyles}
                    disabled={submitting || !valid}
                    loading={submitting}
                    size="large"
                    variant="contained"
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};

const radioFieldStyles: SxProp = {
  mb: 2,
  [`& .${radioClasses.checked}`]: {
    borderColor: 'red',
  },
  [`& .${radioClasses.root}`]: {
    mb: 1,
    fontSize: '1rem',
  },
};

const boxStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
};
